/**
 * Flashcard Source Navigation Mixin
 *
 * Provides the "go to source position" behavior shared by flashcard views:
 * deriving whether a card points to a navigable location in the book, the
 * localized link label, and the navigation action itself.
 *
 * Requirements:
 * - The consuming component must expose a `sourceCard` computed (or data)
 *   returning the flashcard to navigate from.
 * - It must map the `localizations` and `lang` state, the `isEpub`/`isPdf`/
 *   `onlyMobileVisible` getters, and the `goToPage`/`goToLocation`/
 *   `toggleFlashcardsPanel` actions from the store. On mobile the flashcards
 *   panel is fullscreen, so navigating to the source closes it to reveal the
 *   target position.
 *
 * @example
 * import { mapActions, mapGetters, mapState } from 'vuex';
 * import flashcardSourceMixin from '../../js/mixins/flashcardSourceMixin';
 *
 * export default {
 *     mixins: [flashcardSourceMixin],
 *     computed: {
 *         ...mapState(['localizations', 'lang']),
 *         ...mapGetters(['isEpub', 'isPdf', 'onlyMobileVisible']),
 *         sourceCard() {
 *             return this.card;
 *         },
 *     },
 *     methods: {
 *         ...mapActions(['goToPage', 'goToLocation', 'toggleFlashcardsPanel']),
 *     },
 * };
 */
export default {
    computed: {
        /**
         * @returns {boolean} Whether the source card has a navigable position
         */
        hasSource() {
            if (this.isPdf && this.sourceCard?.startPage) return true;
            if (this.isEpub && this.sourceCard?.epubR2Location) return true;
            return false;
        },
        /**
         * @returns {string} Label for the source navigation link
         */
        sourceLabel() {
            if (this.isPdf && this.sourceCard?.startPage) {
                return this.localizations[this.lang].flashcard.goToSource.replace('{page}', this.sourceCard.startPage);
            }
            return this.localizations[this.lang].flashcard.goToSourceEpub;
        },
    },
    methods: {
        /**
         * Navigates to the source position of the card in the book.
         */
        navigateToSource() {
            if (this.isPdf && this.sourceCard?.startPage) {
                this.goToPage({ page: this.sourceCard.startPage, trigger: 'click', source: 'flashcards' });
            } else if (this.isEpub && this.sourceCard?.epubR2Location) {
                this.goToLocation({
                    epubR2Location: this.sourceCard.epubR2Location,
                    trigger: 'click',
                    source: 'flashcards',
                });
            } else {
                return;
            }

            if (this.onlyMobileVisible) {
                this.toggleFlashcardsPanel();
            }
        },
    },
};
